<template>
  <div class="relative w-full h-full bg-gray-800 viewer-container" :style="{ overflow: 'clip' }">
    <slot name="top-bar">
      <EditorDeckEditSceneBar
        v-if="isEditingIndividualScene"
        class="absolute top-0 left-0 right-0"
        :disabled="isTweening"
        :is-composing-new-scene="isComposingNewScene"
        :is-saving-as-scene="isSavingAsScene"
        :loading="loading"
        :slide-title="slideTitle"
        :show-slide-title="showSlideTitle"
        :progress="progress"
        @update:slide-title="$emit('update:slide-title', $event)"
        @update:show-slide-title="$emit('update:show-slide-title')"
        @save="saveCamera"
        @save-as="saveCameraAs"
        @back="handleEditSceneBack"
        @cancel="handleEditSceneCancel"
      />
      <EditorModelVisibilityTitleOverlay
        v-else-if="scopingElement"
        :scoping-element="scopingElement as LayerSceneElement"
        :layer-groups="threeDViewer.layerGroups.value"
        @back="$emit('back')"
      />
    </slot>
    <div class="progress-container">
      <div
        class="progress"
        :style="{
          '--progress-amount': progress,
          width: `${progress * 100}%`,
          opacity: progress < 1 ? 1 : 0,
        }"
      />
    </div>
    <EditorLabelControls v-if="selectedTextLabelId" :selected-label-id="selectedTextLabelId" :labels-manager="threeDViewer.labelsManager" />
    <div
      ref="container"
      class="threed-elements"
      :class="[{ controlling: controlEvents.isControlling }, activeTool]"
      @mousedown.right="handleContextMenu"
      @mouseup.right="confirmContextMenu"
    >
      <LoadingSpinner v-if="progress < 0.1" class="absolute z-10 top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 transform" size="sm" />

      <EditorModelEditorLabelsView
        :labels-manager="threeDViewer.labelsManager"
        :scoping-element="scopingElement"
        :selected-id="selectedTextLabelId"
        :is-model-tweening="isModelTweening"
        :disabled="activeTool === 'none'"
        :loading-progress="threeDViewer.progress.value"
        @resize="onLabelResize"
        @blur="deselectAndBlurLabels"
      />
      <div
        v-if="activeTool === 'none' || activeTool === 'select'"
        class="absolute z-30 w-4 h-4 -m-2 bg-white rounded-full pointer-events-none center-indicator mix-blend-difference transition-opacity duration-300"
        :class="[isControlling ? 'opacity-100' : 'opacity-0']"
        :style="{ top: '50%', left: '50%' }"
      >
        &nbsp;
      </div>
    </div>

    <VNotification
      class="absolute w-full text-center bottom-4 max-w-144 left-1/2 -translate-x-1/2 transform"
      :show="!!selectedTextLabelId"
      translucent
      type=""
      variant="dark"
      inert
    >
      <template #title>
        Any label update will sync across all slides leveraging model
        <CubeIcon class="inline w-5 mx-1" />
        {{ threeDViewer.world.currentModel && threeDViewer.world.currentModel.id }}
      </template>
    </VNotification>

    <VNotification class="info-notification" :show="isComposingNewScene && !!threeDViewer.layerGroups.value.length" type="info">
      <template #title>
        Show or hide other 3D objects by clicking the &nbsp;<span class="text-gray-700"><EyeIcon class="inline-block w-4" /></span>&nbsp;
        icon in the layers list.
      </template>
    </VNotification>

    <div v-if="selectedLegends.length" class="absolute flex w-full h-full justify-end items-end gap-8 p-8 pointer-events-none">
      <DeckAssayLegend
        v-for="legend in numericalAssayLegends"
        :key="legend.id"
        :legend="legend as DeepReadonly<NumericalLegend>"
        :is-grade-bin-active="isGradeBinActive && selectedLegend?.id === legend.id"
        :default-color="selectedGradeBinColor"
        class="pointer-events-auto"
        data-type="assay-legend"
        @select-color="updateLegendColor"
      />
      <DeckCategoricalLegend
        v-for="legend in categoricalAssayLegends"
        :key="legend.id"
        :legend="legend as DeepReadonly<CategoricalLegend>"
        :is-grade-bin-active="isGradeBinActive && selectedLegend?.id === legend.id"
        :default-color="selectedGradeBinColor"
        class="pointer-events-auto"
        data-type="assay-legend"
        @select-color="updateLegendColor"
      />
    </div>

    <Portal v-if="editMode === 'write' && activated" to="editor-details">
      <div
        class="flex flex-col flex-grow overflow-y-auto text-white bg-gray-800 _3d-layers-list"
        tabindex="0"
        data-intercom-target="3d-layers-list"
        @focus="setFocused(true)"
        @blur="setFocused(false)"
      >
        <EditorDeckSlidesThreeDSearchableLayerList
          v-show="threeDViewer.world.currentModel"
          class="flex-grow"
          :layer-groups="layerListGroupsWithLegends"
          :search="threeDViewer.search.value"
          :disabled="isModelTweening || isSavingAsScene"
          :readonly="!userState.flags.MANAGE_3D_LABELS_ENABLED"
          :selected-layers="selectedLayers as ThreeDElement[]"
          :three-d-viewer="threeDViewer"
          :scoping-element="scopingElement"
          :show-search="!scopingElement"
          :show-text-scale="showTextScale"
          :selected-label-id="selectedTextLabelId"
          :selected-drill-layer-group="selectedDrillLayerGroup"
          :selected-legend="selectedLegend as Option"
          @set-layer-visible="onSetLayerVisible"
          @layer-hover="onLayerHover"
          @contextmenu="openContextMenu"
          @collapsible-header-click="selectDrillLayerGroup"
          @click="handleLayerClick"
          @update-text-scale="updateTextScale"
          @save-text-scale="$emit('save-text-scale', threeDViewer.world.currentModel?.getTextScale())"
          @set-search="threeDViewer.setSearch"
        />
      </div>
      <template v-if="selectedDrillLayerGroup || selectedDrillLabelId || selectedDrillLayer || selectedDrillHoleLayer">
        <EditorDeckSlidesThreeDDataConfigPanel
          v-if="activeConfig"
          class="flex-shrink-0"
          :size="size"
          :is-grade-bin-active="isGradeBinActive"
          :font-options="fontOptions"
          :is-auto-scale="isAutoScale"
          :selected-legend="selectedLegend as Option"
          :legends="legends"
          :active-config="activeConfig"
          :selected-data-set="selectedDataSet"
          @size-change="setSize"
          @select-legend="selectLegend"
          @toggle-auto-scale="toggleAutoScale"
          @update-collar-config="updateCollarConfig"
          @update-survey-config="updateSurveyConfig"
          @update-highlight-config="updateAssayConfig"
          @toggle-grade-bins-visibility="toggleGradeBinVisibility"
        />
      </template>
    </Portal>
    <Portal to="editor-modals">
      <EditorDeckModalsRemoveDrillData
        v-if="drillDataToDelete"
        :drill-data-set-name="drillDataToDelete.name"
        :showing="!!drillDataToDelete"
        @update:showing="drillDataToDelete = null"
        @remove-data-set="drillDataToDelete && removeDrillDataFromModel(drillDataToDelete)"
      />
    </Portal>
    <slot />
  </div>
</template>

<script lang="ts" setup>
import type { DeepReadonly, PropType } from 'vue'
import { computed, nextTick, onActivated, onDeactivated, onMounted, onUnmounted, ref, watch } from 'vue'
import { Portal } from 'portal-vue'
import type { Object3D } from 'three'
import { Vector2, Mesh, Group } from 'three'
import type CameraControls from 'camera-controls'
import { EyeIcon, CubeIcon } from '@heroicons/vue/24/solid'

import { ControlEvent } from '@/lib/three-d-resources/controls/viewerControls'
import type { LayerSceneElement, SceneElement } from '@/entities/model3d'
import type { Category, ThreeDElement, ThreeDVisibleElement } from '@/composables/useLayerSelect'
import type { LabelToolTypes } from '@/lib/three-d-viewer/tools/ToolManager'
import { ToolManager } from '@/lib/three-d-viewer/tools/ToolManager'
import {
  isCategory,
  isDrillDataSetGroup,
  isDrillLabel,
  isDrillLegend,
  isDrillLayer,
  isClusterLayer,
  isLayer,
  isLayerCategory,
  isTextLabel,
  pointerEventToVec2,
  selectableId,
  toggleElementVisibility,
  toggleGroupLabelVisibility,
} from '@/lib/three-d-editor/utils'
import { getEditControls } from '@/lib/three-d-editor/edit-controls'
import type { LayerGroup, UseThreeDViewer } from '~/composables/useThreeDViewer'
import { isDrillDataSetLayerGroup } from '~/composables/useThreeDViewer'
import { SelectionTool } from '~/lib/three-d-viewer/tools/SelectionTool'
import { TextLabelCreationTool } from '~/lib/three-d-viewer/tools/TextLabelCreationTool'
import type { LabelsManager, TextLabel } from '~/lib/three-d-resources/labels-manager'
import type { Layer } from '~/composables/useThreeDLayerGroups'
import type { DrillDataSetLayerGroup } from '~/composables/useDrillDataSetGroups'
import type { DrillLayer, DrillLabelLayer, DrillCSVFile } from '~/entities/drillLabel'
import type { DrillDataSet, DrillLabelApiData } from '~/entities/drillLabel'
import type { ThreeDEditMode } from '~/entities/model3d'
import type { Option } from '~/components/VDropdown.vue'
import { disposeScene } from '~/lib/three-d-viewer/utils'

import type { DrillLabelVisibility, LabelVisibility, NodeVisibility } from '@/entities/model3d'
import { isDrillLabelVisibility } from '@/entities/model3d'
import type { DrillLegendViewModel, NumericalLegend, CategoricalLegend } from '~/entities/drillHole'
import type { DrillDataConfig } from '~/entities/drillHole'

type ContextMenuOption = {
  id: string
  label: string
  isExternal?: boolean
  call(context: ThreeDElementContext): void
}

type ThreeDElementContext = {
  labelsManager: LabelsManager
  elements: (TextLabel | Layer | Category)[]
  controls?: CameraControls
}

const props = defineProps({
  editMode: {
    type: String as PropType<ThreeDEditMode>,
    default: 'read',
  },
  contextMenu: {
    type: Object as PropType<{ show: boolean; context: any }>,
    required: true,
  },
  activeTool: {
    type: String as PropType<LabelToolTypes>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isComposingNewScene: {
    type: Boolean,
    default: false,
  },
  isSavingAsScene: {
    type: Boolean,
    required: true,
  },
  isDuplicatingScene: {
    type: Boolean,
    default: false,
  },
  isEditingIndividualScene: {
    type: Boolean,
    default: false,
  },
  showScenes: {
    type: Boolean,
    default: false,
  },
  scopingElement: {
    type: Object as PropType<SceneElement | null>,
    default: null,
  },
  showTextScale: {
    type: Boolean,
    default: false,
  },
  slideTitle: {
    type: String as PropType<string | null>,
    default: '',
  },
  showSlideTitle: {
    type: Boolean,
    default: true,
  },
  threeDViewerError: {
    type: String as PropType<string | null>,
    default: '',
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
  progress: {
    type: Number,
    required: true,
  },
  companyId: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits([
  'back',
  'save',
  'blur-labels',
  'cancel',
  'change',
  'contextmenu',
  'create-scene',
  'manage-slide-visibility',
  'move-flagpole',
  'notify',
  'change-model-drill-data-localization',
  'remove-drill-data-from-model',
  'reposition-flagpole',
  'save-as',
  'save-text-scale',
  'select-label',
  'update-camera-preview',
  'update:activeTool',
  'update:show-slide-title',
  'update:slide-title',
])

const { state: userState, hasRole } = useUser()
const { $analytics, $sentry } = useNuxtApp()
const selectedTextLabelId = ref<string | null>(null)
const selectedDrillLabelId = ref<number | null>(null)
const selectedDrillLayerGroup = ref<DrillDataSet | undefined>()
const selectedDrillLayer = ref<DrillLayer | null>(null)
const isTweening = ref(false)
const isModelTweening = computed(() => isTweening.value) // TODO: remove this
const container = ref<HTMLElement | null>(null)
const containerResizeObserver = new ResizeObserver(_invalidate)
const controlEvents = useThreeDControlEvents()
const rightClickStartPoint = new Vector2()
const drillDataToDelete = ref<DrillDataSetLayerGroup | null>(null)

const isControlling = ref(false)

watch(controlEvents.isControlling, newValue => {
  isControlling.value = newValue
})

const showErrorModal = ref(false)
const errorModalCustomMessage = ref('')
const controls = getEditControls()
const hasV1BvhBeenCalculated = new Set<number>()

const toolManager = new ToolManager()
const selectionTool = new SelectionTool(props.threeDViewer.world)
const labelCreationTool = new TextLabelCreationTool(props.threeDViewer.world)

const selectLabel = (e: { textLabel?: TextLabel; drillLabel?: Object3D; label?: TextLabel }) => {
  if (e.hasOwnProperty('label') && !e.label) {
    return
  }

  if (e.textLabel) {
    if (e.textLabel.id !== selectedTextLabelId.value) {
      selectedTextLabelId.value = e.textLabel.id
      selectedDrillLabelId.value = null
    }
  } else if (e.drillLabel) {
    const drillLabelId = e.drillLabel.userData.drillLabelId
    if (drillLabelId !== selectedDrillLabelId.value) {
      selectedDrillLabelId.value = drillLabelId
      selectedTextLabelId.value = null
    }
  } else if (selectedTextLabelId.value || selectedDrillLabelId.value) {
    deselectAndBlurLabels()
  }
}

selectionTool.addEventListener('label-select', e => {
  selectLabel(e)

  if (!e.textLabel) return

  const eventName = e.isRepositioning ? 'reposition-flagpole' : 'move-flagpole'
  emit(eventName, e.textLabel.id)
})
labelCreationTool.addEventListener('label-select', selectLabel)
labelCreationTool.addEventListener('label-select', () => emit('update:activeTool', 'select'))

toolManager.registerTool(selectionTool)
toolManager.registerTool(labelCreationTool)
toolManager.setActiveTool(props.activeTool)

//- Layers for the selectable layer list
const layers = computed(() => {
  return (props.threeDViewer.layerGroups.value as LayerGroup[]).flatMap((group: LayerGroup) => {
    if (!isDrillDataSetLayerGroup(group)) {
      return group.layers.flatMap((l: Layer | TextLabel) => l as ThreeDVisibleElement)
    }

    const drillLayers = group.layers as DrillLayer[]
    const labels = drillLayers.flatMap((l: DrillLayer) => l.files.flatMap((f: DrillCSVFile) => f.labels)) as DrillLabelLayer[]

    return [...drillLayers, ...labels]
  })
})

const selectedDrillHoleLayer = ref<DrillLayer | null>(null)

const {
  selectedLayers,
  setFocused,
  selectLayer,
  clearLayerSelection,
  handleLayerClick: _handleLayerClick,
} = useLayerSelect({
  layers,
  getSelectableId: selectableId as (v: DeepReadonly<ThreeDElement>) => string,
  focusLabel: id => {
    // _fitLabelInView(label)
  },
  focusLayer: id => {
    // _fitLayerInView(id)
    // _highlightLayer(id)
  },
})

const {
  options: legends,
  currentOption: selectedLegend,
  loadOptions,
  selectOption: _selectLegend,
} = useDropdown<DrillLegendViewModel>({ reducer: (acc, { id, name }: DrillLegendViewModel) => acc.concat({ id, name }) })

const {
  selectedLegends,
  layerListGroupsWithLegends,
  removeLegend,
  toggleLegendVisibility,
  getDefaultLegend,
  getLegendsForDataSet,
  setActiveLegend,
  removeSelectedLegendsForDataSet,
} = useAssayLegends({
  threeDViewer: props.threeDViewer,
})

const {
  activeConfig,
  isGradeBinActive,
  selectedGradeBinColor,
  selectedDataSet,
  updateCollarConfig,
  updateSurveyConfig,
  updateAssayConfig,
  updateLegendColor,
  toggleGradeBinVisibility,
  numericalAssayLegends,
  categoricalAssayLegends,
} = useDrillHoleSettings({
  threeDViewer: props.threeDViewer,
  editMode: toRef(props, 'editMode'),
  selectedLegends,
  selectedLegend,
  selectedDrillLayerGroup,
  selectedLayers,
  onDataSetSelected: (dataSetId: number) => {
    if (selectedLegend.value) return

    const legends = getLegendsForDataSet(dataSetId)
    const legend = getDefaultLegend(dataSetId)

    if (!legend) return

    loadOptions(legends)
    _selectLegend({ id: legend.id, name: legend.name })
  },
})

const { size, fontOptions, isAutoScale, setSize, toggleAutoScale } = useLabelTextSize({
  drillDataSetManager: props.threeDViewer.world.drillDataSetManager,
  drillDataSet: selectedDataSet,
})

watch(
  [() => layers.value, props.threeDViewer.search.value, () => props.editMode, () => props.slideTitle],
  ([layers, search, mode, title]: any, [oldLayers, oldSearch, _, oldTitle]: any) => {
    if (mode === 'read') {
      _selectLegend(null)
      return
    }

    // Resorted to using setTimeout instead of nextTick because of the async nature of the dropdown
    setTimeout(() => {
      const isDataSetRemovedOrAdded = layers.length !== oldLayers.length && !search && !oldSearch
      const hasTitleChanged = title !== oldTitle && selectedLegend.value

      if (isDataSetRemovedOrAdded || !selectedLegend.value || hasTitleChanged) {
        const defaultLegend = getDefaultLegend()

        if (!defaultLegend) {
          selectClusterLayer()
          return
        }

        const defaultDrillLayer = layers.find((l: any) => (l as DrillLayer).id === defaultLegend.id) as DrillLayer
        const layerGroup = getDrillLayerGroupForLegends(props.threeDViewer.layerGroups.value as LayerGroup[], defaultDrillLayer)
        const legends = getLegendsForDataSet(layerGroup?.dataSetId as number)

        // dropdown operations
        loadOptions(legends)
        _selectLegend({ id: defaultLegend.id, name: defaultLegend.name })

        // legend visibility operations
        selectedDrillHoleLayer.value = defaultDrillLayer
        selectLayer(defaultDrillLayer)
      }
    }, 500)
  },
)

watch(selectedLayers, selectedLayers => {
  const selectableLayerTypes = ['drill', 'legend', 'cluster']

  if (!selectedLayers.length || !selectedLayers.some((l: any) => selectableLayerTypes.includes(l.type))) {
    selectedDrillHoleLayer.value = null
  }
})

function selectLegend(legend: Option) {
  selectLayer((layers.value as DrillLayer[]).find(l => l.id === legend.id) as DrillLayer)

  removeLegend(selectedLegend.value!.id as string)

  const drillLegend = selectedLayers.value.find(layer => 'id' in layer && layer.id === legend.id)

  if (drillLegend) {
    setActiveLegend(legend.id as string)
  }

  _selectLegend(legend)
}

function selectClusterLayer() {
  const clusterLayer = (layers.value as DrillLayer[]).find((layer: DrillLayer) => 'type' in layer && layer.type === 'cluster')

  if (!clusterLayer) return

  selectedDrillHoleLayer.value = clusterLayer as DrillLayer
  selectLayer(clusterLayer as DrillLayer)
}

function deselectAndBlurLabels() {
  if (!selectedTextLabelId.value) return
  selectedTextLabelId.value = ''
  nextTick().then(_invalidate)
}

function handleLayerClick(event: PointerEvent, item: DeepReadonly<ThreeDVisibleElement>) {
  _handleLayerClick(event, item)

  const isNormalClick = event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey
  if (isNormalClick) selectedDrillLayerGroup.value = undefined

  const drillLabelLayer = selectedLayers.value.find(l => isDrillLabel(l as ThreeDElement)) as DrillLabelLayer
  selectedDrillLabelId.value = drillLabelLayer ? (drillLabelLayer.id as number) : null

  const drillHoleLayer = selectedLayers.value.find(
    l => isDrillLayer(l as ThreeDElement) && (l as DrillLayer).type === 'legend',
  ) as unknown as DrillLayer
  if (drillHoleLayer) {
    nextTick(() => {
      selectedDrillHoleLayer.value = drillHoleLayer
      selectedDrillLayerGroup.value = undefined
      selectedDrillLabelId.value = null
    })

    const dataSetId = getDrillLayerGroupForLegends(props.threeDViewer.layerGroups.value, item as DrillLayer)?.dataSetId
    const legends = getLegendsForDataSet(dataSetId as number)
    const selectedLegend = legends.find((l: Option) => l.id === (item as DrillLayer).id) as Option

    loadOptions(legends)
    if (selectedLegend) _selectLegend(selectedLegend)
  } else {
    selectedDrillHoleLayer.value = null
  }

  const drillLayer = selectedLayers.value.find(l => isDrillLayer(l as ThreeDElement) && !isDrillLegend(l as ThreeDElement)) as DrillLayer
  selectedDrillLayer.value = drillLayer || null
}

function clearDrillLayerSelection() {
  loadOptions([])
  selectedDrillHoleLayer.value = null
}

function getDrillLayerGroupForLegends(layerGroups: LayerGroup[], drillLayer: DrillLayer | undefined): DrillDataSetLayerGroup {
  return layerGroups.find(group => {
    if (drillLayer?.type !== 'legend') return false
    return (group.layers as DrillLayer[]).some((layer: DrillLayer) => layer.id === drillLayer.id)
  }) as DrillDataSetLayerGroup
}

let needsRender = true
let frameId: number | null = null

watch(
  () => props.activeTool,
  activeTool => {
    toolManager.setActiveTool(activeTool)

    if (activeTool === 'label-creation') {
      // viewer?.setHighlightLayerColor(HIGHLIGHT_COLORS.GREEN)
    } else if (activeTool === 'select') {
      // viewer?.setHighlightLayerColor(HIGHLIGHT_COLORS.BLUE)
    }

    _invalidate()
  },
)

watch(
  () => props.threeDViewerError,
  message => {
    if (message) {
      showErrorModal.value = true
      errorModalCustomMessage.value = message
    }
  },
  { immediate: true },
)

// @todo on cancel click
// async function clickCancel() {
//   $analytics.track('DeckEditor3DSceneEditorClickCancel', {
//     deckName: deck.value?.title,
//     deckId: deck.value?.id,
//     modelId: slide.value?.model_id,
//   })
// }

watch(
  () => props.contextMenu.show,
  show => {
    if (!show) {
      // props.model?.layerList.forEach(layer => {
      //   viewer?.highlightLayer(layer.id, false)
      // })
      props.threeDViewer.world.layerHighlightManager?.removeCurrentLayerHighlight()
    }
  },
)

// watch(scopingElement, (scopingElement, oldScope) => {
//   if (oldScope) {
//     viewer?.scopeByElement(oldScope, false)
//   }
//   if (scopingElement) {
//     viewer?.scopeByElement(scopingElement, true)
//     if (isScopedLayer(scopingElement)) {
//       _fitLayerInView(scopingElement.nodeId)
//     } else if (isScopedLabel(scopingElement)) {
//       const label = props.labelsManager.getLabel(scopingElement.labelId)
//       if (!label) return
//
//       if (!label.visible && !props.isEditingIndividualScene) {
//         props.labelsManager.updateLabel(label.id, { visible: true })
//       }
//
//       _fitLabelInView(label)
//     }
//     controls.disableTruck()
//   }
//   if (!scopingElement) {
//     controls.enableTruck()
//   }
//   _invalidate()
// })

const deselectClickOutsideCanvas = (e: PointerEvent) => {
  if (!container.value?.contains(e.target as Node)) {
    deselectAndBlurLabels()
  }
}

onMounted(async () => {
  if (!container.value) {
    throw new Error('Failed to mount viewer. Containers were nullish')
  }

  toolManager.attach(container.value)
  containerResizeObserver.observe(container.value)
  props.threeDViewer.world.mount(container.value)

  document.addEventListener('pointerup', deselectClickOutsideCanvas)
  document.addEventListener('keydown', _handleDeleteKey)

  _activate()

  // Start the animation loop
  _onFrame()
})

onUnmounted(() => {
  if (frameId) cancelAnimationFrame(frameId)
  containerResizeObserver.disconnect()
  document.removeEventListener('pointerup', deselectClickOutsideCanvas)
  document.removeEventListener('keydown', _handleDeleteKey)
  _deactivate()
})

function selectDrillLayerGroup(layerGroup: LayerGroup) {
  clearLayerSelection()
  selectedDrillLabelId.value = null

  if (isDrillDataSetLayerGroup(layerGroup)) {
    selectedDrillLayerGroup.value = props.threeDViewer.world.drillDataSetManager?.getDrillDataSetAggregate(layerGroup.dataSetId)
    clearDrillLayerSelection()
  } else {
    selectedDrillLayerGroup.value = undefined
  }
}

function _handleDeleteKey(e: KeyboardEvent) {
  const isInputActive = document.activeElement?.tagName === 'INPUT'
  if (!selectedTextLabelId.value || isInputActive || (e.key !== 'Delete' && e.key !== 'Backspace')) return
  e.preventDefault()

  // TODO: Delete drill labels?
  props.threeDViewer.labelsManager.removeLabel(selectedTextLabelId.value)
  deselectAndBlurLabels()
}

function onSetLayerVisible(param: NodeVisibility[] | LabelVisibility[] | DrillLabelVisibility[]) {
  if (isDrillLabelVisibility(param)) {
    toggleLegendVisibility({ ...param[0], id: param[0].drillLabelId as string })
  }

  emit('change')
  _invalidate()
}

function onLayerHover({ nodeId, hover }: { nodeId: number; hover: boolean }) {
  if (props.contextMenu.show) return

  props.threeDViewer.world.layerHighlightManager?.setLayerHighlight({ layerId: nodeId, shouldHighlight: hover })
  _invalidate()
}

function updateTextScale(value: number) {
  const currentModelTextScale = props.threeDViewer.world.currentModel?.getTextScale()
  const scaleChange = currentModelTextScale ? currentModelTextScale / value : 1

  props.threeDViewer.world.currentModel?.setTextScale(value)
  props.threeDViewer.labelsManager.setGlobalTextScale(value)
  props.threeDViewer.labelsManager.rebuildLabels(scaleChange)
}

function handleContextMenu(e: MouseEvent) {
  rightClickStartPoint.set(e.clientX, e.clientY)
}

watch(
  () => props.editMode,
  mode => {
    clearLayerSelection()
    selectedDrillLabelId.value = null
    selectedDrillLayerGroup.value = undefined
    if (mode === 'read') {
      emit('update:activeTool', 'none')
      props.threeDViewer.labelsManager.setMode('view')
      props.threeDViewer.world.layerHighlightManager?.removeCurrentLayerHighlight()
    } else if (mode === 'write' && userState.value.flags.MANAGE_3D_LABELS_ENABLED) {
      emit('update:activeTool', 'select')
      props.threeDViewer.labelsManager.setMode('edit')

      if (hasRole('internal_admin')) props.threeDViewer.world.drillDataSetManager?.setMode('edit')

      //TODO: when migrating away from v1 models, delete this
      if (
        props.threeDViewer.world.currentModel?.id &&
        !hasV1BvhBeenCalculated.has(props.threeDViewer.world.currentModel.id) &&
        props.threeDViewer.world.currentModel.apiData.version === 1
      ) {
        hasV1BvhBeenCalculated.add(props.threeDViewer.world.currentModel.id)
        props.threeDViewer.world.currentModel.scene.traverse(node => {
          if (node instanceof Mesh) {
            node.geometry.computeBoundsTree()
          }
        })
      }
    }
  },
)

// TODO: Implement this setting scene using focus first layer
// function todo (argument: argument_type) {
//   if (focusFirstLayer) {
//        this.cancelTween()
//        const { position, target } = this.viewer.getLayerSceneZoom(this.viewer.layers[0].nodeId)
//        let { x: px, y: py, z: pz } = position
//        const { x: tx, y: ty, z: tz } = target
//        // adding to py so we look down on the model as per VRFY-10970 to avoid facing the edge of a flat plane
//        py = ty + position.distanceTo(target) / 2
//        controls.setLookAt(px, py, pz, tx, ty, tz)
//        return
//      }
// }

function confirmContextMenu(e: MouseEvent) {
  const distanceMoved = rightClickStartPoint.distanceTo({ x: e.clientX, y: e.clientY } as Vector2)
  const normalized = pointerEventToVec2(e)

  if (props.editMode !== 'write') return

  if (distanceMoved < 5) {
    const hit = props.threeDViewer.world?.hitTest(normalized, { includeLabels: true })
    if (!hit) return // context menu should only appear if we hit an object
    if (!(hit.label || hit.drillLabel || hit.nodeId)) return

    let elem: ThreeDElement

    if (hit.label) {
      elem = hit.label
    } else if (hit.drillLabel) {
      elem = layers.value.filter(isDrillLabel).find(layer => layer.id === hit.drillLabel!.userData.drillLabelId)!
    } else {
      elem = layers.value.filter(isLayer).find(layer => layer.nodeId === hit.nodeId)!
      props.threeDViewer.world.layerHighlightManager?.setLayerHighlight({ layerId: elem.nodeId, shouldHighlight: true })
    }

    openContextMenu(e, elem, { in3DViewer: true })
    selectLayer(elem)
  } else {
    // else perform a camera pan and remove layer highlight
    props.threeDViewer.world.layerHighlightManager?.removeCurrentLayerHighlight()
  }
}

function openContextMenu(
  event: MouseEvent,
  elementOrElements: ThreeDElement | ThreeDElement[],
  opts: { in3DViewer?: boolean; definitelyShown?: boolean } = {},
): void {
  if (!Array.isArray(elementOrElements)) return openContextMenu(event, [elementOrElements], opts)

  let elements = elementOrElements // makes TypeScript happy
  const onlyTextLabels = elements.every(isTextLabel)
  const onlyDrillLabels = elements.every(isDrillLabel)
  const onlyDrillLegends = elements.every(isDrillLegend)
  const onlyLayers = elements.every(isLayer)
  const onlyCategory = elements.every(isCategory)
  const onlyDrillLayers = elements.every(isDrillLayer)
  const isOnlyLayerCategory = elements.every(isLayerCategory)
  const isOnlyDrillDataSet = elements.every(isDrillDataSetGroup)

  // important this happens after the 'only___' assignments
  if (elements.every(isLayerCategory) && elements.length === 1) {
    elements = elements[0].layers
  }

  if (onlyDrillLayers && !elements.every(isClusterLayer)) {
    const drillIds = (elements as DrillLayer[]).map(d => d.id)
    elements = props.threeDViewer.layerGroups.value
      .filter(isDrillDataSetLayerGroup)
      .flatMap(d => d.layers)
      .filter(d => drillIds.includes(d.id))
      .flatMap(l => l.files)
      .flatMap(f => f.labels)
  }

  if (onlyDrillLegends) {
    elements = elementOrElements
  }

  const hideOrShow = {
    label: opts.definitelyShown || opts.in3DViewer ? 'Hide' : 'Hide/Show',
    id: 'hide-or-show',
    call: () => {
      if (elements.length === 1 && isDrillDataSetGroup(elements[0])) {
        elements = (elements[0] as DrillDataSetLayerGroup).layers
          .map(val => (!isDrillDataSetGroup(val) ? [val] : val.files.flatMap(f => f.labels)))
          .flat()
      }
      toggleElementVisibility({ threeDViewer: props.threeDViewer, elements })
      emit('change')
    },
  }

  const focusInThreeDViewer = {
    label: 'Focus in 3D viewer',
    id: 'focus-in-viewer',
    call: () => {
      if (isCategory(elements[0])) return

      if (isTextLabel(elements[0])) {
        const object = props.threeDViewer.labelsManager.getLabelObject(elements[0].id)
        props.threeDViewer.world.focusObjectInViewer(object!)
      } else if (isLayer(elements[0])) {
        const sceneNode = props.threeDViewer.world.currentModel!.sceneNodesById.get(elements[0].nodeId)
        props.threeDViewer.world.focusObjectInViewer(sceneNode!)
      } else if (isDrillLabel(elements[0])) {
        const drillIds = elements.map(l => (l as DrillLabelLayer).id)
        const objects = drillIds.map(
          l => props.threeDViewer.world.drillDataSetManager?.drillLabelManager.getDrillLabelObject(l as number)!.labelOriginGroup,
        )
        const boundingGroup = new Group()
        objects.forEach(o => o && boundingGroup.add(o.clone()))
        props.threeDViewer.world.focusObjectInViewer(boundingGroup)
        disposeScene(boundingGroup)
      }
    },
  }

  const deleteLabels = {
    label: 'Delete',
    id: 'delete-labels',
    call: () => {
      for (const label of elements.filter(isTextLabel)) {
        props.threeDViewer.labelsManager.removeLabel(label.id)
      }
      emit('change')
    },
  }

  // hide/show all labels (including parent layers) belonging to category
  const hideOrShowLabels = {
    label: 'Hide/Show all label layers',
    id: 'hide-or-show-labels',
    call: () => {
      toggleGroupLabelVisibility({ threeDViewer: props.threeDViewer, elements: elements as Layer[] })
      emit('change')
    },
  }

  const editDataConfiguration = {
    label: 'Edit data configuration',
    isExternal: true,
    id: 'edit-data-set',
    call: () => {
      if (!isCategory(elements[0])) return
      window.open(`${window.location.origin}/data-files/settings/${elements[0].dataSetId}?companyId=${props.companyId}`, '_blank')
    },
  }

  const editLocalization = {
    label: 'Change origin localization',
    id: 'edit-localization',
    call: () => {
      if (!isCategory(elements[0])) return
      emit('change-model-drill-data-localization', elements[0] ?? null)
    },
  }

  const removeDrillDataFromModel = {
    label: 'Remove drill data from model',
    id: 'remove-drill-data',
    call: () => {
      if (!isCategory(elements[0])) return
      drillDataToDelete.value = { ...elements[0], name: elements[0].categoryName } as unknown as DrillDataSetLayerGroup
    },
  }

  const manageSlideVisibility = {
    label: 'Manage slide visibility',
    id: 'manage-slide-visibility',
    call: () => emit('manage-slide-visibility', elements[0]),
  }

  const toggleFlagpole = {
    label: 'Toggle flagpole',
    id: 'toggle-flagpole',
    call: () => {
      for (const label of elements.filter(isTextLabel)) {
        props.threeDViewer.labelsManager.updateLabel(label.id, { flagpoleVisible: !label.flagpoleVisible })
      }
      emit('change')
    },
  }

  const activateLabelCreationMode = {
    label: 'Create new label',
    id: 'activate-label-creation-mode',
    call: () => emit('update:activeTool', 'label-creation'),
  }

  const collapseOrExpandDrillLabels = {
    label: 'Collapse/expand all drill labels in data set',
    id: 'collapse-or-expand-drill-labels',
    call: () => {
      if (!isDrillLabel(elements[0])) return
      const dataSetId = props.threeDViewer.world.drillDataSetManager?.getParentDataSet(elements[0].id as number)?.data_set_id
      if (!dataSetId) return
      props.threeDViewer.world.drillDataSetManager?.toggleAllCollapsed(dataSetId)
      emit('change')
    },
  }

  const collapseOrExpandDataSet = {
    label: 'Collapse/expand all drill labels in data set',
    id: 'collapse-or-expand-data-set',
    call: () => {
      if (!isDrillDataSetGroup(elements[0])) return
      const dataSetId = elements[0].dataSetId
      props.threeDViewer.world.drillDataSetManager?.toggleAllCollapsed(dataSetId)
      emit('change')
    },
  }

  const resetLabelName = {
    label: 'Reset label name',
    id: 'reset-label-name',
    call: async () => {
      const drillId = (elements[0] as any).id
      const drillLabel = props.threeDViewer.world.drillDataSetManager?.drillLabelManager.getDrillLabelObject(drillId)
      const { apiData } = drillLabel || {}

      if (!apiData) return

      props.threeDViewer.world.drillDataSetManager?.drillLabelManager.resetDrillLabelName(drillId, {
        edited_collapsed_intercept_info: null,
        edited_expanded_intercept_info: null,
        edited_sub_intercept_info: null,
      })
    },
  }

  let options: ContextMenuOption[] = [hideOrShow]

  if (onlyTextLabels || onlyLayers || onlyDrillLabels) {
    if (onlyLayers && elements.length === 1 && !props.isEditingIndividualScene) {
      options.unshift(manageSlideVisibility)
    }
    if (elements.length === 1) {
      if (!opts.in3DViewer) {
        options.unshift(focusInThreeDViewer)
      } else if (props.isEditingIndividualScene && props.activeTool !== 'label-creation' && onlyLayers) {
        options.push(activateLabelCreationMode)
      }
    }
    if (onlyTextLabels) {
      options.push(toggleFlagpole)
      options.push(deleteLabels)
    }
    if (onlyDrillLayers) {
      options.unshift(focusInThreeDViewer)
    }

    if (onlyDrillLabels) {
      options.push(collapseOrExpandDrillLabels)

      const drillLabel = props.threeDViewer.world.drillDataSetManager?.drillLabelManager.getDrillLabelObject((elements[0] as any).id)
      const { apiData } = drillLabel || {}

      if (hasRole('internal_admin') && apiData && _hasDrillLabelBeenEdited(apiData)) options.push(resetLabelName)
    }
  } else if (onlyCategory) {
    hideOrShow.label = 'Hide/Show all layers'
    if (isOnlyLayerCategory) {
      options.push(hideOrShowLabels)
    }
    if (isOnlyDrillDataSet && userState.value.flags.MANAGE_DRILL_LABELS_ENABLED) {
      if (props.companyId) {
        options.push(editDataConfiguration)
      }

      if (props.isEditingIndividualScene) {
        options.push(editLocalization)
        options.push(removeDrillDataFromModel)
      }
    }
    if (isOnlyDrillDataSet) {
      options.push(collapseOrExpandDataSet)
    }
  }

  emit('contextmenu', {
    event: event,
    context: null,
    options,
  })
}

function _hasDrillLabelBeenEdited(apiData: DrillLabelApiData) {
  const isEditingCollapsedInterceptInfo =
    apiData.edited_collapsed_intercept_info && apiData.edited_collapsed_intercept_info !== apiData.collapsed_intercept_info

  const isEditingExpandedInterceptInfo =
    apiData.edited_expanded_intercept_info && apiData.edited_expanded_intercept_info !== apiData.expanded_intercept_info

  const isEditingSubInterceptInfo = apiData.edited_sub_intercept_info && apiData.edited_sub_intercept_info !== apiData.sub_intercept_info

  return isEditingCollapsedInterceptInfo || isEditingExpandedInterceptInfo || isEditingSubInterceptInfo
}

function _onViewChanged() {
  isTweening.value = true

  _invalidate()
}

function _onMousedown(e: MouseEvent) {
  emit('change')
  if (!selectedTextLabelId.value) controlEvents.startControl(e)
}

function _onFrame() {
  frameId = requestAnimationFrame(_onFrame)

  const cameraUpdated = controls.update()

  if (props.threeDViewerError) {
    cancelAnimationFrame(frameId!)
    return
  }

  const alwaysRender = props.editMode === 'write'

  if (!(alwaysRender || cameraUpdated || needsRender || props.threeDViewer.world.needsRender)) {
    return
  }

  props.threeDViewer.world.render()

  needsRender = false
}

function _stopTween() {
  isTweening.value = false
}

function _invalidate() {
  needsRender = true
}

function _handleOnSaveCallback(): void {
  // viewer?.disposeHighlights()
}

function createScene() {
  // _handleSceneUpdate({ title: getTimestampNow(), emitEvent: 'create-scene' })
}

function cancelEdit() {
  // viewer?.disposeHighlights()
}

function saveCamera(title: string) {
  controls.halt()
  emit('save', title)
}

function saveCameraPreview(title: string) {
  controls.halt()
  emit('update-camera-preview', title)
}

function saveCameraAs(title: string) {
  controls.halt()
  emit('save-as', title)
}

const activated = ref(false)

onActivated(_activate)
onDeactivated(_deactivate)

function _activate() {
  controls.addEventListener(ControlEvent.StopTween, _stopTween)
  controls.addEventListener(ControlEvent.CameraControlEvent, _onViewChanged)

  container.value?.addEventListener('mousedown', _onMousedown)
  container.value?.addEventListener('mouseup', controlEvents.stopControl)
  container.value?.addEventListener('wheel', controlEvents.wheel)

  selectionTool.addEventListener('label-move', _invalidate)

  activated.value = true
}

function _deactivate() {
  controls.removeEventListener(ControlEvent.StopTween, _stopTween)
  controls.removeEventListener(ControlEvent.CameraControlEvent, _onViewChanged)

  container.value?.removeEventListener('mousedown', _onMousedown)
  container.value?.removeEventListener('mouseup', controlEvents.stopControl)
  container.value?.removeEventListener('wheel', controlEvents.wheel)

  selectionTool.removeEventListener('label-move', _invalidate)

  activated.value = false
}

function onLabelResize(params: { width: number; height: number; label: TextLabel }) {
  props.threeDViewer.onLabelResize(params)
  _invalidate()
}

async function removeDrillDataFromModel(drillData: DrillDataSetLayerGroup) {
  if (!props.threeDViewer.world.drillDataSetManager) return
  try {
    await props.threeDViewer.world.drillDataSetManager.removeDataSet(drillData.dataSetId)
    selectedDrillLayerGroup.value = undefined
    selectedDrillLabelId.value = null
    clearLayerSelection()
    clearSelectedLegends(drillData.dataSetId)
    emit('notify', {
      title: `Drill data removed`,
      type: 'success',
    })
  } catch (e) {
    $sentry?.captureException(e)
    emit('notify', {
      title: 'An error occurred',
      text: 'Drill data was not removed successfully',
      type: 'danger',
    })
  }
  drillDataToDelete.value = null
}

function clearSelectedLegends(dataSetId: number) {
  loadOptions([])
  selectedDrillHoleLayer.value = null
  removeSelectedLegendsForDataSet(dataSetId)
}

watch(() => props.threeDViewer.search.value, clearLayerSelection)

function handleEditSceneBack() {
  cancelEdit()
  emit('back')
}

function handleEditSceneCancel() {
  cancelEdit()
  emit('cancel')
}
</script>

<style lang="stylus" scoped>
:deep(.modal-overlay)
  @apply absolute
.viewer-container
  background radial-gradient(#4a4a4a, #212121)
  .progress-container
    @apply p-1 absolute top-0 left-0 z-50 w-full
  .progress
    @apply h-2 bg-white
    transition-property opacity, width, background-color
    transition-duration 400ms, 500ms, 200ms
    transition-delay 1000ms, 0ms, 0ms
    transition-timing-function ease-out

@css {
  .viewer-container .progress {
    background-color: rgba(255, 255, 255, calc(var(--progress-amount) / 2 + 0.5))
  }
}
.info-notification
  @apply absolute top-24 w-full px-8 z-10

.threed-elements
  @apply h-full w-full absolute
  &.controlling
    @apply cursor-grab
  &.label-creation
    @apply cursor-text
  //&.select
  //&.none
</style>
